import React from 'react';
import axios from 'axios';
import { BASELINE } from '../util';

// 기본 데이터 구조
const linear_table_data = {
    x_values: [0, 1, 2],
    f_x_values: [29, 32, 35],
    correctFunction: { slope: 3, intercept: 29 },
    options: {
        tableWidth: 300,
        cellPadding: 10,
        fontSize: 18,
        borderColor: "#000",
        borderWidth: 1,
        showAltText: false
    }
};

// 테이블 렌더링 컴포넌트
const Set_linear_table = ({ graphData }) => {
    // graphData가 없으면 기본값을 사용
    const {
        x_values,
        f_x_values,
        correctFunction,
        options = {}
    } = graphData || linear_table_data;

    // 옵션 설정
    const {
        tableWidth = 300,
        cellPadding = 10,
        fontSize = 18,
        borderColor = "#000",
        borderWidth = 1,
        showAltText = false
    } = options;

    // 컬럼 너비 계산
    const colWidth = tableWidth / 2;

    // Alt 텍스트 생성
    const generateAltText = () => {
        if (!correctFunction) {
            return `This table shows values of a linear function. ` +
                `For x values of ${x_values.join(', ')}, the corresponding f(x) values are ${f_x_values.join(', ')}.`;
        }

        const { slope, intercept } = correctFunction;
        return `This table shows values of the linear function f(x) = ${slope}x + ${intercept}. ` +
            `For x values of ${x_values.join(', ')}, the corresponding f(x) values are ${f_x_values.join(', ')}. ` +
            `Each f(x) value is calculated by multiplying x by ${slope} and adding ${intercept}.`;
    };

    const altText = generateAltText();

    return (
        <div>
            <svg
                width={tableWidth}
                height={(x_values.length + 1) * (fontSize * 2)}
                xmlns="http://www.w3.org/2000/svg"
                aria-labelledby="tableDescription"
                role="img"
            >
                <title id="tableDescription">Linear Function Table</title>
                <desc>{altText}</desc>

                {/* Table Headers */}
                <rect x="0" y="0" width={colWidth} height={fontSize * 2}
                    fill="none" stroke={borderColor} strokeWidth={borderWidth} />
                <rect x={colWidth} y="0" width={colWidth} height={fontSize * 2}
                    fill="none" stroke={borderColor} strokeWidth={borderWidth} />

                <text x={colWidth / 2} y={fontSize + cellPadding}
                    textAnchor="middle" fontSize={fontSize} fontFamily="serif" fontStyle="italic">x</text>
                <text x={colWidth + colWidth / 2} y={fontSize + cellPadding}
                    textAnchor="middle" fontSize={fontSize} fontFamily="serif" fontStyle="italic">f(x)</text>

                {/* Table Rows */}
                {x_values.map((x, index) => (
                    <g key={index}>
                        <rect x="0" y={(index + 1) * (fontSize * 2)} width={colWidth} height={fontSize * 2}
                            fill="none" stroke={borderColor} strokeWidth={borderWidth} />
                        <rect x={colWidth} y={(index + 1) * (fontSize * 2)} width={colWidth} height={fontSize * 2}
                            fill="none" stroke={borderColor} strokeWidth={borderWidth} />

                        <text x={colWidth / 2} y={(index + 1) * (fontSize * 2) + fontSize + cellPadding}
                            textAnchor="middle" fontSize={fontSize}>{x}</text>
                        <text x={colWidth + colWidth / 2} y={(index + 1) * (fontSize * 2) + fontSize + cellPadding}
                            textAnchor="middle" fontSize={fontSize}>{f_x_values[index]}</text>
                    </g>
                ))}
            </svg>

            {/* Display alt text if requested */}
            {showAltText && (
                <div style={{ marginTop: '10px', fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                    {altText}
                </div>
            )}
        </div>
    );
};

// AI 응답을 처리하기 위한 함수
const LinearFunctionTable_prompt = async (count, difficulty = "M") => {
    try {
        const getPerfectJSON = async (data, maxAttempts = 5) => {
            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                try {
                    const res = await axios.post(`${BASELINE}askai3`, data);
                    if (res.data && res.data.message) {
                        try {
                            const ai_response = JSON.parse(res.data.message);
                            return ai_response; // Return the parsed JSON if successful
                        } catch (error) {
                            console.warn(`Attempt ${attempt}: JSON parsing error, retrying...`);
                        }
                    } else {
                        console.warn(`Attempt ${attempt}: Unexpected response structure, retrying...`);
                    }
                } catch (error) {
                    console.warn(`Attempt ${attempt}: axios request error (${error.message}), retrying...`);
                }
            }
            // After maxAttempts, use the npm toggle package to display a warning message.
            return null;
        };

        // SVG 코드 생성 함수
        const generateSVGCode = (data) => {
            if (!data) return { svgCode: "", altText: "" };

            const { x_values, f_x_values, correctFunction, options = {} } = data;
            const {
                tableWidth = 300,
                cellPadding = 10,
                fontSize = 18,
                borderColor = "#000",
                borderWidth = 1
            } = options;

            const colWidth = tableWidth / 2;
            const tableHeight = (x_values.length + 1) * (fontSize * 2);

            // Alt 텍스트 생성
            let altText = "";
            if (!correctFunction) {
                altText = `This table shows values of a linear function. ` +
                    `For x values of ${x_values.join(', ')}, the corresponding f(x) values are ${f_x_values.join(', ')}.`;
            } else {
                const { slope, intercept } = correctFunction;
                altText = `This table shows values of the linear function f(x) = ${slope}x + ${intercept}. ` +
                    `For x values of ${x_values.join(', ')}, the corresponding f(x) values are ${f_x_values.join(', ')}. ` +
                    `Each f(x) value is calculated by multiplying x by ${slope} and adding ${intercept}.`;
            }

            // SVG 코드 생성
            const svgCode = `<svg width="${tableWidth}" height="${tableHeight}" xmlns="http://www.w3.org/2000/svg" aria-labelledby="tableDescription" role="img">
  <title id="tableDescription">Linear Function Table</title>
  <desc>${altText}</desc>

  <!-- Table Headers -->
  <rect x="0" y="0" width="${colWidth}" height="${fontSize * 2}" fill="none" stroke="${borderColor}" stroke-width="${borderWidth}" />
  <rect x="${colWidth}" y="0" width="${colWidth}" height="${fontSize * 2}" fill="none" stroke="${borderColor}" stroke-width="${borderWidth}" />

  <text x="${colWidth / 2}" y="${fontSize + cellPadding}" text-anchor="middle" font-size="${fontSize}" font-family="serif" font-style="italic">x</text>
  <text x="${colWidth + colWidth / 2}" y="${fontSize + cellPadding}" text-anchor="middle" font-size="${fontSize}" font-family="serif" font-style="italic">f(x)</text>

  <!-- Table Rows -->
  ${x_values.map((x, index) => `
  <g>
    <rect x="0" y="${(index + 1) * (fontSize * 2)}" width="${colWidth}" height="${fontSize * 2}" fill="none" stroke="${borderColor}" stroke-width="${borderWidth}" />
    <rect x="${colWidth}" y="${(index + 1) * (fontSize * 2)}" width="${colWidth}" height="${fontSize * 2}" fill="none" stroke="${borderColor}" stroke-width="${borderWidth}" />

    <text x="${colWidth / 2}" y="${(index + 1) * (fontSize * 2) + fontSize + cellPadding}" text-anchor="middle" font-size="${fontSize}">${x}</text>
    <text x="${colWidth + colWidth / 2}" y="${(index + 1) * (fontSize * 2) + fontSize + cellPadding}" text-anchor="middle" font-size="${fontSize}">${f_x_values[index]}</text>
  </g>`).join('')}
</svg>`;

            return { svgCode, altText };
        };

        const graphPromises = [];

        // Convert difficulty code to descriptive text.
        let difficultyText = "medium";
        if (difficulty === "E") difficultyText = "easy";
        if (difficulty === "H") difficultyText = "hard";

        for (let i = 0; i < count; i++) {
            const data = {
                prompt: `
            Generate a random JSON object representing data for a table that displays values for a linear function at ${difficultyText} difficulty level. The table should have two columns: one for the x values and one for f(x) values, where f(x) = slope * x + intercept.
            
            Schema:
            {
              "x_values": [number, number, number, ...],
              "f_x_values": [number, number, number, ...],
              "correctFunction": {
                "slope": number,
                "intercept": number
              },
              options: {
                tableWidth: 300,
                cellPadding: 10,
                fontSize: 18,
                borderColor: "#000",
                borderWidth: 1,
                showAltText: false
                },
              "distractors": [
                {
                  "slope": number,
                  "intercept": number
                },
                {
                  "slope": number,
                  "intercept": number
                },
                {
                  "slope": number,
                  "intercept": number
                }
              ]
            }
            
            Additional instructions for ${difficultyText} difficulty:
            ${difficulty === "E"
                        ? "- Use small positive integers (1-5) for slope and positive integers (1-20) for intercept.\n- Use x values of 0, 1, 2 only.\n- Make distractors clearly different from the correct function."
                        : difficulty === "M"
                            ? "- Use integers (-10 to 10, but not 0) for slope and integers (-20 to 20) for intercept.\n- Use sequential x values including at least one negative value.\n- Create plausible distractors by swapping values or using nearby integers."
                            : "- Use non-integer values for slope (like 1.5, 2.5, etc.) or larger integers (-15 to 15, but not 0).\n- Use non-sequential x values that include negative numbers.\n- Create challenging distractors that are mathematically related to the correct function."
                    }
            
            - The "x_values" array should contain at least 3 numeric entries.
            - The "f_x_values" array must be generated using the formula f(x) = slope * x + intercept for each corresponding x in "x_values".
            - The "correctFunction" field must include the slope and intercept used to compute the f(x) values.
            - The "distractors" array should include 3 alternative pairs of slope and intercept values.
            - Output only the JSON object and nothing else.
          `
            };

            graphPromises.push(
                getPerfectJSON(data).then((ai_response) => {
                    if (ai_response) {
                        // SVG 코드와 alt text 생성
                        const { svgCode, altText } = generateSVGCode(ai_response);

                        return {
                            component: Set_linear_table({
                                graphData: {
                                    ...ai_response,
                                    options: { showAltText: false }
                                }
                            }),
                            data: ai_response,
                            svgCode: svgCode,
                            altText: altText
                        };
                    } else {
                        return null;
                    }
                })
            );
        }

        const results = await Promise.all(graphPromises);
        return results;
    } catch (error) {
        console.error("Error in LinearFunctionTable_prompt:", error);
        return null;
    }
};

export {
    linear_table_data,
    Set_linear_table,
    LinearFunctionTable_prompt
};